import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  FaArrowRight,
  FaCashRegister,
  FaEye,
  FaEyeSlash,
  FaExclamationCircle,
  FaLock,
  FaUser,
} from "react-icons/fa";
import Footer from "../components/CommonComponents/Footer";
import SubscriptionExpiredModal from "../components/CommonComponents/SubscriptionExpiredModal";

export default function POSLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [expiredSubscription, setExpiredSubscription] = useState(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleLogin = async () => {
    if (isLoggingIn) return;

    if (!username.trim() || !password.trim()) {
      setLoginError("Please enter both username and password.");
      return;
    }

    setLoginError("");
    setIsLoggingIn(true);
    const result = await login({ username: username.trim(), password }, [2]);
    setIsLoggingIn(false);
    if (result.success) {
      navigate("/dashboard");
      return;
    }

    if (result.code === "SUBSCRIPTION_EXPIRED") {
      setExpiredSubscription(result.details || {});
      setPassword("");
      return;
    }

    setLoginError(result.error || "Login failed.");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleLogin();
  };

  return (
    <div
      className="relative flex min-h-screen flex-col overflow-hidden bg-[#071224]"
      onKeyDown={handleKeyPress}
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(56,189,248,0.18),_transparent_24%),radial-gradient(circle_at_10%_20%,_rgba(251,191,36,0.14),_transparent_26%),linear-gradient(135deg,_#041124_0%,_#08213d_45%,_#020617_100%)]" />
        <div className="absolute left-0 top-16 h-72 w-72 rounded-full bg-cyan-300/10 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-amber-200/10 blur-3xl" />
      </div>

      <div className="relative flex flex-1 items-center justify-center px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid w-full max-w-5xl overflow-hidden rounded-[28px] border border-white/10 bg-white/8 shadow-[0_32px_80px_rgba(2,8,23,0.45)] backdrop-blur-xl lg:grid-cols-[1.05fr_0.95fr]">
          <div className="hidden flex-col justify-between border-b border-white/10 px-6 py-8 sm:px-10 lg:flex lg:border-b-0 lg:border-r">
            <div>
              <div className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.32em] text-slate-100">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-300 text-sm text-slate-950">
                  <FaCashRegister />
                </span>
                Point of sale
              </div>

              <div className="mt-8 space-y-4">
                <h1 className="max-w-md text-4xl font-semibold leading-tight text-white sm:text-5xl">
                  Point of Sale System
                </h1>
                <p className="max-w-lg text-sm leading-7 text-slate-300 sm:text-base">
                  Sign in to manage sales, process transactions, and continue
                  daily store operations.
                </p>
              </div>
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-white/6 p-4">
                <p className="text-xs uppercase tracking-[0.22em] text-slate-400">
                  Sales
                </p>
                <p className="mt-2 text-sm font-medium text-white">
                  Access the cashier dashboard and handle customer transactions
                  quickly.
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/6 p-4">
                <p className="text-xs uppercase tracking-[0.22em] text-slate-400">
                  Operations
                </p>
                <p className="mt-2 text-sm font-medium text-white">
                  View products, monitor records, and continue branch activity
                  from one place.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white px-6 py-8 sm:px-10 lg:px-12 lg:py-10">
            <div className="mx-auto flex h-full w-full max-w-md flex-col justify-center">
              <div className="mb-8">
                <div className="mb-5 inline-flex items-center gap-3 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.32em] text-slate-700 lg:hidden">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-300 text-sm text-slate-950">
                    <FaCashRegister />
                  </span>
                  Point of sale
                </div>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">
                      ZeroOne POS
                    </p>
                    <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900">
                      Welcome back
                    </h2>
                  </div>
                  <div
                    aria-hidden="true"
                    className="pointer-events-none mt-1 inline-flex shrink-0 select-none items-center gap-2 rounded-full border border-slate-900/15 bg-slate-900/95 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-white shadow-sm"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                    Staff login
                  </div>
                </div>
                <p className="mt-4 max-w-sm text-sm leading-6 text-slate-500">
                  Sign in to access sales, transactions, and daily POS tools.
                </p>
              </div>

              <div className="space-y-5">
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-slate-700">
                    Username or Email
                  </span>
                  <div className="group relative">
                    <FaUser className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 transition group-focus-within:text-slate-900" />
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value);
                        if (loginError) setLoginError("");
                      }}
                      placeholder="Enter your username or email"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-12 pr-4 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-slate-900 focus:bg-white focus:ring-4 focus:ring-slate-200/70"
                      autoComplete="username"
                    />
                  </div>
                </label>

                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-slate-700">
                    Password
                  </span>
                  <div className="group relative">
                    <FaLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 transition group-focus-within:text-slate-900" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (loginError) setLoginError("");
                      }}
                      placeholder="Enter your password"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-12 pr-12 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-slate-900 focus:bg-white focus:ring-4 focus:ring-slate-200/70"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute top-1/2 right-4 -translate-y-1/2 text-slate-500 transition hover:text-slate-900 focus:outline-none"
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </label>

                <div className="flex items-center justify-between gap-4 text-xs text-slate-500">
                  <p>Use your branch account to continue.</p>
                  <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">
                    Counter ready
                  </span>
                </div>

                <button
                  onClick={handleLogin}
                  disabled={isLoggingIn}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-[0_16px_30px_rgba(15,23,42,0.18)] transition hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-wait disabled:opacity-70 focus:outline-none focus:ring-4 focus:ring-slate-300"
                >
                  {isLoggingIn ? "Signing in..." : "Continue to POS"}
                  <FaArrowRight className="text-xs" />
                </button>

                {loginError ? (
                  <div className="rounded-2xl border border-rose-200 bg-gradient-to-r from-rose-50 via-white to-rose-50 px-4 py-3 shadow-sm shadow-rose-100/70">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rose-100 text-sm text-rose-600">
                        <FaExclamationCircle />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-rose-700">
                          Sign in failed
                        </p>
                        <p className="mt-1 text-sm leading-5 text-rose-600">
                          {loginError}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer
        className="relative py-4 text-xs"
        variant="light"
        noBorder
        noPadding
        showLegalLinks
        companyHref="https://zerooneitinc.com/"
      />
      <SubscriptionExpiredModal
        isOpen={Boolean(expiredSubscription)}
        details={expiredSubscription}
        onClose={() => setExpiredSubscription(null)}
      />
    </div>
  );
}
